import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/sonner";
import HomePage from "@/pages/HomePage";
import AdminPage from "@/pages/AdminPage";
import VideoPage from "@/pages/VideoPage";
import LoginPage from "@/pages/LoginPage";
import MusicPage from "@/pages/MusicPage";
import GamesListPage from "@/pages/GamesListPage";
import GamePage from "@/pages/GamePage";
import ShopPage from "@/pages/ShopPage";
import TerminosPage from "@/pages/TerminosPage";
import PrivacidadPage from "@/pages/PrivacidadPage";
import ContactPage from "@/pages/ContactPage";
import MediaKitPage from "@/pages/MediaKitPage";
import MediaKitLicenciamientoPage from "@/pages/MediaKitLicenciamientoPage";
import MediaKitMusicaPage from "@/pages/MediaKitMusicaPage";
import StickersPage from "@/pages/StickersPage";
import { PromoBanner } from "@/components/PromoBanner";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Secret keyboard shortcut handler
function SecretAdminAccess() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+Shift+A to access admin
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        navigate('/login');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  return null;
}

// Dynamic site settings loader (favicon & tagline)
function SiteSettingsLoader() {
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(`${API}/site-settings`);
        const settings = await res.json();
        
        // Update favicon
        if (settings.favicon_url) {
          let favicon = document.querySelector("link[rel='icon']");
          if (!favicon) {
            favicon = document.createElement('link');
            favicon.rel = 'icon';
            document.head.appendChild(favicon);
          }
          favicon.href = settings.favicon_url;
        }
        
        // Update tagline in title
        if (settings.tagline) {
          const baseTitle = "Darkar Company";
          document.title = `${baseTitle} | ${settings.tagline}`;
        }
      } catch (error) {
        console.error('Error loading site settings:', error);
      }
    };
    
    loadSettings();
  }, []);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <div className="App relative">
        {/* Background collage fixed - across entire site */}
        <div 
          className="fixed inset-0 pointer-events-none"
          style={{
            backgroundImage: 'url(https://customer-assets-0z36b82j.emergentagent.net/job_6374765b-0927-4fa7-ace6-9a95eb25e587/artifacts/2z6zdrcy_COLLAGE012000.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed',
            zIndex: 0
          }}
        />
        <div className="relative z-10">
          <Toaster position="top-right" theme="dark" />
          <BrowserRouter>
            <SiteSettingsLoader />
            <SecretAdminAccess />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/video/:videoId" element={<VideoPage />} />
              <Route path="/musica" element={<MusicPage />} />
              <Route path="/juegos" element={<GamesListPage />} />
              <Route path="/juegos/:gameId" element={<GamePage />} />
              <Route path="/tienda" element={<ShopPage />} />
              <Route path="/terminos" element={<TerminosPage />} />
              <Route path="/privacidad" element={<PrivacidadPage />} />
              <Route path="/contacto" element={<ContactPage />} />
              <Route path="/media-kit" element={<MediaKitPage />} />
              <Route path="/media-kit/licenciamiento" element={<MediaKitLicenciamientoPage />} />
              <Route path="/media-kit/musica" element={<MediaKitMusicaPage />} />
              <Route path="/stickers" element={<StickersPage />} />
            </Routes>
            <PromoBanner />
            <PWAInstallPrompt />
          </BrowserRouter>
        </div>
      </div>
    </HelmetProvider>
  );
}

export default App;
